import React, { useState, useEffect } from 'react';
import type { Customer, Reservation } from '../../types';
import { customerService, PaginatedResponse } from '../../services/customerServiceSupabase';
import { Loader2, AlertCircle, User, Phone, Edit, Users, Hash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { useTenant } from '../../contexts/TenantContext';

interface CustomerDetailsProps {
  customerId: string;
  onEdit: (customer: Customer) => void;
  onClose: () => void;
}

export const CustomerDetails: React.FC<CustomerDetailsProps> = ({ customerId, onEdit, onClose }) => {
  const { tenantId } = useTenant();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [reservations, setReservations] = useState<PaginatedResponse<Reservation> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!tenantId) throw new Error('Δεν έχει επιλεγεί επιχείρηση');
        const customerData = await customerService.getCustomerById(tenantId, customerId);
        setCustomer(customerData);
        const reservationData = await customerService.getCustomerReservations(customerId);
        setReservations(reservationData);
      } catch (err) {
        setError('Αποτυχία φόρτωσης στοιχείων πελάτη.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [customerId]);

  const handlePageChange = async (page: number) => {
    if (!reservations || !customer) return;
    try {
        const reservationData = await customerService.getCustomerReservations(customer.id, page, reservations.pagination.limit);
        setReservations(reservationData);
    } catch (err) {
        setError('Αποτυχία φόρτωσης κρατήσεων για αυτή τη σελίδα.');
    }
  };

  const translateStatus = (status: string | undefined) => {
    switch ((status || '').toLowerCase()) {
      case 'confirmed':
        return 'Επιβεβαιωμένη';
      case 'completed':
        return 'Ολοκληρωμένη';
      case 'cancelled':
        return 'Ακυρωμένη';
      case 'no-show':
        return 'Δεν εμφανίστηκε';
      case 'pending':
        return 'Εκκρεμεί';
      case 'seated':
        return 'Καθισμένος';
      default:
        return status || '-';
    }
  };

  const statusClass = (status: string | undefined) => {
    const key = (status || '').toLowerCase();
    if (key === 'confirmed' || key === 'completed' || key === 'seated') return 'text-green-600';
    if (key === 'cancelled') return 'text-red-600';
    if (key === 'no-show') return 'text-muted-foreground';
    return '';
  };

  const derivedCounts = React.useMemo(() => {
    const all = reservations?.data || [];
    const total = customer?.reservation_count ?? all.length;
    const confirmed = customer?.confirmed_reservations ?? all.filter(r => ['confirmed', 'completed', 'seated'].includes((r as any).status)).length;
    const cancelled = customer?.cancelled_reservations ?? all.filter(r => ['cancelled', 'no-show'].includes((r as any).status)).length;
    return { total, confirmed, cancelled };
  }, [customer, reservations]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-8 text-center text-red-600 bg-red-50 rounded-lg">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>{error || 'Customer not found.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 md:space-y-6 max-h-[75vh] overflow-y-auto pr-1 md:pr-0">
      {/* Customer Profile Card */}
      <div className="rounded-lg border bg-card p-3 sm:p-4 space-y-4 shadow-sm">
        <div className="flex justify-between items-start gap-2">
            <div>
                <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center"><User className="mr-2 h-6 w-6"/>{customer.name}</h2>
                <p className="text-muted-foreground flex items-center text-sm sm:text-base"><Phone className="mr-2 h-4 w-4"/>{customer.phone}</p>
            </div>
            <Button variant="outline" onClick={() => onEdit(customer)} className="shrink-0 gap-2">
              <Edit className="h-4 w-4" />
              <span className="hidden sm:inline">Επεξεργασία</span>
            </Button>
        </div>
        {customer.notes && (
          <p className="text-sm text-foreground bg-amber-50 dark:bg-amber-900/30 p-3 rounded-md border border-amber-200 dark:border-amber-800">
            {customer.notes}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center pt-2">
            <div>
                <p className="text-xs text-muted-foreground uppercase">Σύνολο Κρατήσεων</p>
                <p className="text-lg sm:text-xl font-bold text-foreground">{derivedCounts.total}</p>
            </div>
            <div>
                <p className="text-xs text-muted-foreground uppercase">Επιβεβαιωμένες</p>
                <p className="text-lg sm:text-xl font-bold text-green-600">{derivedCounts.confirmed}</p>
            </div>
            <div>
                <p className="text-xs text-muted-foreground uppercase">Ακυρωμένες</p>
                <p className="text-lg sm:text-xl font-bold text-red-600">{derivedCounts.cancelled}</p>
            </div>
        </div>
      </div>

      {/* Reservation History */}
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-2">Ιστορικό Κρατήσεων</h3>
        <div className="rounded-lg border bg-card shadow-sm py-3 sm:py-4 px-0">
             <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-[11px] sm:text-xs font-medium text-muted-foreground uppercase">Ημερομηνία</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-[11px] sm:text-xs font-medium text-muted-foreground uppercase">Κατάσταση</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-[11px] sm:text-xs font-medium text-muted-foreground uppercase">Τραπέζι</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-[11px] sm:text-xs font-medium text-muted-foreground uppercase">Άτομα</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {reservations && reservations.data.map(res => (
                        <tr key={res.id}>
                            <td className="p-3 sm:p-4 whitespace-nowrap">
                                <div className="font-medium text-foreground text-sm sm:text-base">{format(new Date(`${res.date}T${res.time}`), 'dd MMM yyyy, HH:mm', { locale: el })}</div>
                            </td>
                            <td className={`p-3 sm:p-4 whitespace-nowrap text-sm sm:text-base ${statusClass((res as any).status)}`}>
                              {translateStatus((res as any).status)}
                            </td>
                            <td className="p-3 sm:p-4 whitespace-nowrap text-foreground text-sm sm:text-base"><Hash className="inline mr-1 h-4 w-4" />{res.table?.number || res.table_id || 'N/A'}</td>
                            <td className="p-3 sm:p-4 whitespace-nowrap text-foreground text-sm sm:text-base"><Users className="inline mr-1 h-4 w-4" />{res.party_size}</td>
                        </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {reservations && reservations.data.length === 0 && (
                <p className="p-4 text-center text-muted-foreground text-sm sm:text-base">Δεν υπάρχουν κρατήσεις για αυτόν τον πελάτη.</p>
            )}
            {reservations && reservations.pagination.pages > 1 && (
                 <div className="p-3 sm:p-4 border-t flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                        Σελίδα {reservations.pagination.page} από {reservations.pagination.pages}
                    </span>
                    <div className="space-x-2">
                        <Button 
                        variant="outline"
                        onClick={() => handlePageChange(reservations.pagination.page - 1)}
                        disabled={reservations.pagination.page <= 1}
                        >
                        Προηγούμενη
                        </Button>
                        <Button 
                        variant="outline"
                        onClick={() => handlePageChange(reservations.pagination.page + 1)}
                        disabled={reservations.pagination.page >= reservations.pagination.pages}
                        >
                        Επόμενη
                        </Button>
                    </div>
                </div>
            )}
        </div>
      </div>
      <div className="pt-1 flex justify-end">
        <Button onClick={onClose} variant="outline" className="min-w-[96px]">Κλείσιμο</Button>
      </div>
    </div>
  );
};
